/**
 * Four working demonstrations, on the page, before you sign up.
 *
 * These were drawings that animated at you once and then sat still. They are
 * now small working copies of the real controls: pick a person and the grid
 * filters, drag the exposure slider and the photograph changes under it, choose
 * a ratio and the crop frame moves. Nothing is faked — the sliders are real
 * range inputs driving a real CSS filter, and the filtering is real filtering
 * over a real list.
 *
 * This is the strongest thing the page can do. A stranger who has just been
 * told the product is invite-only and encrypted can *use* a piece of it in the
 * next scroll, which no amount of copy achieves.
 *
 * Consequences worth stating:
 *
 *  - **The stages are no longer `aria-hidden`.** They were, when they were
 *    decoration. Hiding a subtree that now contains focusable controls would
 *    put a keyboard user inside something a screen reader says is not there —
 *    a worse bug than the one it would be papering over.
 *  - **Real controls, not divs with click handlers.** `<input type="range">`
 *    and `<button>` are keyboard-operable, announce themselves, and behave the
 *    way people's hands already expect.
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { type Locale, useLocale, useT } from "../../lib/i18n.js";
import { plural, type PluralForms } from "../../lib/plural.js";
import { CopyIcon, CropIcon, FacesIcon, SlidersIcon } from "./icons.js";
import "./photoTiles.css";
import "./Showcase.css";

export function Showcase() {
  const t = useT();

  return (
    <section className="fc-showcase" id="how">
      <div className="fc-landing__band-head" data-reveal>
        <h2>{t("What it actually does")}</h2>
        <p>
          {t(
            "Four pieces of the real thing, working, right here. No account, no upload, nothing to install.",
          )}
        </p>
      </div>

      <div className="fc-showcase__grid">
        <Demo
          icon={<CopyIcon />}
          title="Six phones, four copies of the same shot"
          caption="It finds the near-identical ones — not just the exact copies — and lets you keep the one you actually want."
        >
          <DuplicatesDemo />
        </Demo>

        <Demo
          icon={<FacesIcon />}
          title="Name someone once"
          caption="Your browser finds the faces, on your device. Tap a person and the library narrows to them."
        >
          <FacesDemo />
        </Demo>

        <Demo
          icon={<SlidersIcon />}
          title="A darkroom, not a shelf"
          caption="Drag them. The original is never rewritten — an edit is a recipe stored beside the file."
        >
          <EditorDemo />
        </Demo>

        <Demo
          icon={<CropIcon />}
          title="Crop and straighten"
          caption="Pick a ratio. Undoable forever, because the crop is a number and not a new file."
        >
          <CropDemo />
        </Demo>
      </div>
    </section>
  );
}

function Demo({
  icon,
  title,
  caption,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  caption: string;
  children: React.ReactNode;
}) {
  const t = useT();

  return (
    <motion.figure
      className="fc-showcase__item"
      data-reveal
      whileHover={{ y: -3 }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
    >
      {/* Not aria-hidden — see the note at the top. It holds real controls. */}
      <div className="fc-showcase__stage">{children}</div>
      <figcaption>
        <span className="fc-showcase__icon" aria-hidden="true">
          {icon}
        </span>
        <strong>{t(title)}</strong>
        <span>{t(caption)}</span>
      </figcaption>
    </motion.figure>
  );
}

/* ── Duplicates ───────────────────────────────────────────────────────────── */

const MEMBERS = [
  { initials: "MK", name: "Marta" },
  { initials: "NW", name: "Nikodem" },
  { initials: "PJ", name: "Patrycja" },
  { initials: "TB", name: "Tom" },
];

/**
 * The same moment, twice.
 *
 * Two large frames rather than a grid, because the demonstration beside this
 * one is a grid and they were reading as the same feature. It is also the
 * honest shape of the real screen: comparing two photographs is something you
 * do at size, not in a thumbnail.
 */
const PAIR = [
  {
    kind: "sunset",
    who: "MK",
    time: "19:42:07",
    size: "4.1 MB",
    sharper: true,
  },
  {
    kind: "sunset",
    who: "NW",
    time: "19:42:09",
    size: "3.8 MB",
    sharper: false,
  },
];

function DuplicatesDemo() {
  const t = useT();
  const [kept, setKept] = useState<number | null>(null);

  return (
    <div className="fc-demo">
      <div className="fc-demo__bar">
        <span className="fc-demo__title">{t("2 seconds apart")}</span>
        <span className="fc-demo__meta">{t("98% alike")}</span>
      </div>

      <div className="fc-demo__pair">
        {PAIR.map((shot, index) => (
          <div
            key={index}
            className="fc-demo__shot"
            data-kept={kept === index || undefined}
            data-dropped={kept !== null && kept !== index ? "" : undefined}
          >
            <span
              className="fc-demo__shotimg fc-phototile"
              data-kind={shot.kind}
            />
            <span className="fc-demo__shotmeta">
              <span className="fc-tabular">{shot.time}</span>
              <span className="fc-tabular">{shot.size}</span>
            </span>
            <button
              type="button"
              onClick={() => setKept(index)}
              aria-pressed={kept === index}
            >
              {kept === index ? t("Kept") : t("Keep this one")}
            </button>
          </div>
        ))}
      </div>

      <p className="fc-demo__note" aria-live="polite">
        {kept === null
          ? t("Nothing is deleted until you say so.")
          : `${t("Freed")} ${PAIR[kept === 0 ? 1 : 0]!.size}. ${t("The other goes to the trash for 30 days.")}`}
      </p>
    </div>
  );
}

/* ── Faces ────────────────────────────────────────────────────────────────── */

const FACE_GRID = [
  { kind: "portrait", people: ["MK"] },
  { kind: "landscape", people: [] },
  { kind: "portrait-two", people: ["MK", "NW"] },
  { kind: "sea", people: [] },
  { kind: "portrait", people: ["MK"] },
  { kind: "forest", people: ["PJ"] },
  { kind: "stone", people: [] },
  { kind: "portrait-two", people: ["MK", "TB"] },
  { kind: "sky", people: ["NW"] },
];

/**
 * "4 photos", in a language with three plurals.
 *
 * Not dictionary entries: the dictionary is keyed on English, and Polish needs
 * two different words where English has one. Every count in this demo is 1, 2
 * or 4 — the range English gets right by accident and Polish does not.
 */
const PHOTO_FORMS: Record<Locale, PluralForms> = {
  en: { one: "photo", other: "photos" },
  pl: { one: "zdjęcie", few: "zdjęcia", many: "zdjęć", other: "zdjęcia" },
};

function FacesDemo() {
  const t = useT();
  const { locale } = useLocale();
  const [person, setPerson] = useState("MK");

  const chosen = MEMBERS.find((m) => m.initials === person) ?? MEMBERS[0]!;
  const count = FACE_GRID.filter((t) => t.people.includes(person)).length;

  return (
    <div className="fc-demo">
      <div
        className="fc-demo__people"
        role="group"
        aria-label={t("Choose a person")}
      >
        {MEMBERS.map((member) => (
          <button
            key={member.initials}
            type="button"
            className="fc-demo__person"
            data-face={MEMBERS.indexOf(member) + 1}
            data-chosen={person === member.initials || undefined}
            aria-pressed={person === member.initials}
            onClick={() => setPerson(member.initials)}
          >
            <span className="fc-demo__avatar">{member.initials}</span>
            <span className="fc-demo__personname">{member.name}</span>
          </button>
        ))}
      </div>

      <div className="fc-demo__bar">
        <span className="fc-demo__chip">{chosen.name}</span>
        {/* Announced politely, so a screen reader hears the count change. */}
        <span className="fc-demo__meta fc-tabular" aria-live="polite">
          {count} {plural(locale, count, PHOTO_FORMS[locale])}
        </span>
      </div>

      <div className="fc-demo__grid" data-cols="3">
        {FACE_GRID.map((tile, index) => (
          <span
            key={index}
            className="fc-demo__cell fc-phototile"
            data-kind={tile.kind}
            data-dim={tile.people.includes(person) ? undefined : ""}
          />
        ))}
      </div>
    </div>
  );
}

/* ── The editor ───────────────────────────────────────────────────────────── */

/**
 * The real controls, driving a real filter.
 *
 * `<input type="range">` rather than a drawn track: it is keyboard-operable
 * without any work, it announces its value, and a thumb people can actually
 * drag is the entire point of putting an editor on a marketing page.
 */
const CONTROLS = [
  { key: "exposure", label: "Exposure", min: -100, max: 100, initial: 28 },
  { key: "contrast", label: "Contrast", min: -100, max: 100, initial: 18 },
  { key: "warmth", label: "Warmth", min: -100, max: 100, initial: -12 },
] as const;

type ControlKey = (typeof CONTROLS)[number]["key"];

function EditorDemo() {
  const t = useT();
  const [values, setValues] = useState<Record<ControlKey, number>>({
    exposure: 28,
    contrast: 18,
    warmth: -12,
  });

  /*
   * The same three knobs the application has, mapped onto CSS filters. Warmth
   * has no filter of its own, so it moves hue and saturation together, which is
   * what warming a picture actually does.
   */
  const filter = [
    `brightness(${String(1 + values.exposure / 250)})`,
    `contrast(${String(1 + values.contrast / 200)})`,
    `saturate(${String(1 + values.warmth / 400)})`,
    `hue-rotate(${String(values.warmth / 12)}deg)`,
  ].join(" ");

  return (
    <div className="fc-demo">
      <div
        className="fc-demo__photo fc-phototile"
        data-kind="sunset"
        style={{ filter }}
      />

      <div className="fc-demo__sliders">
        {CONTROLS.map((control) => (
          <label key={control.key} className="fc-demo__slider">
            <span className="fc-demo__sliderlabel">{t(control.label)}</span>
            <input
              type="range"
              min={control.min}
              max={control.max}
              value={values[control.key]}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [control.key]: Number(event.target.value),
                }))
              }
            />
            <span className="fc-demo__value fc-tabular">
              {values[control.key] > 0 ? "+" : ""}
              {values[control.key]}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* ── Crop ─────────────────────────────────────────────────────────────────── */

const RATIOS = [
  { label: "Any", w: 96, h: 96 },
  { label: "1:1", w: 74, h: 96 },
  { label: "4:5", w: 62, h: 92 },
  { label: "16:9", w: 96, h: 62 },
];

function CropDemo() {
  const t = useT();
  const [ratio, setRatio] = useState(1);
  const chosen = RATIOS[ratio]!;

  return (
    <div className="fc-demo">
      <div className="fc-demo__cropstage">
        <div className="fc-demo__photo fc-phototile" data-kind="landscape" />
        <div
          className="fc-demo__frame"
          style={{
            inlineSize: `${String(chosen.w)}%`,
            blockSize: `${String(chosen.h)}%`,
          }}
        >
          <span data-corner="tl" />
          <span data-corner="tr" />
          <span data-corner="bl" />
          <span data-corner="br" />
        </div>
      </div>

      <div
        className="fc-demo__ratios"
        role="group"
        aria-label={t("Crop ratio")}
      >
        {RATIOS.map((item, index) => (
          <button
            key={item.label}
            type="button"
            data-active={index === ratio || undefined}
            aria-pressed={index === ratio}
            onClick={() => setRatio(index)}
          >
            {t(item.label)}
          </button>
        ))}
      </div>
    </div>
  );
}
